import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { pricingPlans } from '../data/mock';
import { Check, Star } from 'lucide-react';

export const PricingSection = ({ onContactClick }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile && scrollContainerRef.current) {
      const scrollWidth = scrollContainerRef.current.scrollWidth / pricingPlans.length;
      scrollContainerRef.current.scrollTo({
        left: scrollWidth * currentSlide,
        behavior: 'smooth'
      });
    }
  }, [currentSlide, isMobile]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % pricingPlans.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + pricingPlans.length) % pricingPlans.length);
  };

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-emerald-50 to-amber-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-emerald-900 mb-4">
            Transparent Pricing Plans
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Choose the package that best fits your trademark registration needs. All plans include expert guidance and USPTO filing.
          </p>
        </div>

        {/* Desktop Grid View */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {pricingPlans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                plan.recommended
                  ? 'border-amber-500 shadow-xl'
                  : 'border-emerald-200 hover:border-emerald-400'
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center">
                    <Star className="h-4 w-4 mr-1" />
                    Recommended
                  </div>
                </div>
              )}
              
              <CardHeader className={`text-center py-8 rounded-t-lg ${
                plan.recommended ? 'bg-gradient-to-br from-emerald-700 to-emerald-600' : 'bg-emerald-800'
              }`}>
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="text-4xl font-bold text-amber-400 mb-1">{plan.price}</div>
                <div className="text-emerald-100 text-sm">{plan.subtitle}</div>
              </CardHeader>

              <CardContent className="p-6">
                <div className="h-64 overflow-y-auto mb-6 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-gray-100">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <Check className="h-5 w-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-xs text-gray-500 mb-4 text-center">{plan.note}</p>

                <Button
                  onClick={onContactClick}
                  className={`w-full font-semibold py-6 transition-all duration-300 ${
                    plan.recommended
                      ? 'bg-amber-600 hover:bg-amber-700 text-white'
                      : 'bg-emerald-700 hover:bg-emerald-800 text-white'
                  }`}
                >
                  Select {plan.name}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Mobile Slider View */}
        <div className="md:hidden">
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-hidden snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {pricingPlans.map((plan) => (
              <div key={plan.id} className="w-full flex-shrink-0 px-4 snap-center">
                <Card
                  className={`border-2 ${
                    plan.recommended ? 'border-amber-500 shadow-xl' : 'border-emerald-200'
                  }`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center">
                        <Star className="h-4 w-4 mr-1" />
                        Recommended
                      </div>
                    </div>
                  )}
                  
                  <CardHeader className={`text-center py-8 rounded-t-lg ${
                    plan.recommended ? 'bg-gradient-to-br from-emerald-700 to-emerald-600' : 'bg-emerald-800'
                  }`}>
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold text-amber-400 mb-1">{plan.price}</div>
                    <div className="text-emerald-100 text-sm">{plan.subtitle}</div>
                  </CardHeader>

                  <CardContent className="p-6">
                    <div className="h-64 overflow-y-auto mb-6">
                      <ul className="space-y-3">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-gray-700">
                            <Check className="h-5 w-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <p className="text-xs text-gray-500 mb-4 text-center">{plan.note}</p>

                    <Button
                      onClick={onContactClick}
                      className={`w-full font-semibold py-6 ${
                        plan.recommended
                          ? 'bg-amber-600 hover:bg-amber-700 text-white'
                          : 'bg-emerald-700 hover:bg-emerald-800 text-white'
                      }`}
                    >
                      Select {plan.name}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Mobile Navigation Dots */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              onClick={prevSlide}
              className="bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold hover:bg-emerald-800 transition-colors"
            >
              ←
            </button>
            <div className="flex gap-2">
              {pricingPlans.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentSlide === idx ? 'bg-emerald-700 w-8' : 'bg-emerald-300'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={nextSlide}
              className="bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold hover:bg-emerald-800 transition-colors"
            >
              →
            </button>
          </div>
        </div>

        <div className="mt-12 text-center bg-white rounded-xl p-8 max-w-3xl mx-auto shadow-lg">
          <p className="text-gray-700 mb-2">
            <strong className="text-emerald-800">Note:</strong> USPTO government filing fees ($250-$350 per class) are additional and paid directly to the USPTO.
          </p>
          <p className="text-gray-600 text-sm">
            Most businesses file in 1-2 classes. We'll help you determine the correct classification during your free consultation.
          </p>
        </div>
      </div>
    </section>
  );
};
